import { useSearchParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { FolderKanban, Plus } from '@/components/icons';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { NewProjectWizard } from '@/components/NewProjectWizard';
import { ProjectTree } from '@/components/ProjectTree';
import { Button, EmptyState, ErrorNotice, PageHeader, PageLoader } from '@/components/ui';

export function ProjectsPage() {
  const { currentOrgId, can } = useAuth();
  const [params, setParams] = useSearchParams();
  const wizardOpen = params.get('new') === '1';
  const setWizard = (open: boolean) => setParams(open ? { new: '1' } : {}, { replace: true });

  const projects = useQuery({
    queryKey: ['projects', currentOrgId],
    queryFn: () => api.projects(currentOrgId!),
    enabled: !!currentOrgId,
  });

  const canCreate = can('project.create');

  return (
    <div>
      <PageHeader
        title="Test projects"
        description="One project per app you want to test."
        actions={
          canCreate && (
            <Button onClick={() => setWizard(true)} icon={<Plus className="size-3" />}>
              New project
            </Button>
          )
        }
      />
      {!currentOrgId ? (
        <EmptyState title="No organization selected" description="Create or join an organization to start testing." />
      ) : projects.isPending ? (
        <PageLoader />
      ) : projects.isError ? (
        <ErrorNotice error={projects.error} onRetry={() => projects.refetch()} />
      ) : projects.data.length === 0 ? (
        <EmptyState
          icon={<FolderKanban className="size-6" />}
          title="No test projects yet"
          description="Create a project for your website or mobile app. It takes less than a minute."
          action={canCreate && <Button onClick={() => setWizard(true)}>Create your first project</Button>}
        />
      ) : (
        <ProjectTree projects={projects.data} />
      )}
      {currentOrgId && canCreate && <NewProjectWizard orgId={currentOrgId} open={wizardOpen} onClose={() => setWizard(false)} />}
    </div>
  );
}
